/**
 * LiveUpdates Component
 *
 * Listens for WebSocket events and shows toast notifications
 * for important real-time updates.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocketContext } from '@/contexts/WebSocketContext';
import { toast } from '@/components/ui';
import type {
  WebSocketEvent,
  DecisionEventData,
  CapacityEventData,
  AlertEventData,
} from '@/lib/websocket';

export function LiveUpdates() {
  const { on, isConnected } = useWebSocketContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribers: (() => void)[] = [];

    // New decision created
    unsubscribers.push(
      on<DecisionEventData>('decision.created', (event) => {
        const data = event.data;
        const urgencyEmoji = data.urgency === 'critical' ? '\u26a0\ufe0f ' : '';

        toast.decision(
          `${urgencyEmoji}New Decision`,
          `${data.title}`,
          {
            label: 'View',
            onClick: () => navigate(`/decisions/${data.decision_id}`),
          }
        );
      })
    );

    // Decision approved
    unsubscribers.push(
      on<DecisionEventData>('decision.approved', (event) => {
        const data = event.data;
        toast.success('Decision Approved', data.title);
      })
    );

    // Decision rejected
    unsubscribers.push(
      on<DecisionEventData>('decision.rejected', (event) => {
        const data = event.data;
        toast.warning('Decision Rejected', data.title);
      })
    );

    // Capacity alert
    unsubscribers.push(
      on<CapacityEventData>('capacity.alert', (event) => {
        const data = event.data;
        if (data.alert_level === 'critical') {
          toast.error(
            'Capacity Critical',
            `${data.team_name} is at ${data.utilization}% utilization`
          );
        } else if (data.alert_level === 'warning') {
          toast.warning(
            'Capacity Warning',
            `${data.team_name} approaching limit`
          );
        }
      })
    );

    // System alert
    unsubscribers.push(
      on<AlertEventData>('system.alert', (event) => {
        const data = event.data;
        switch (data.severity) {
          case 'critical':
          case 'error':
            toast.error(data.title, data.message);
            break;
          case 'warning':
            toast.warning(data.title, data.message);
            break;
          default:
            toast.info(data.title, data.message);
        }
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [isConnected, on, navigate]);

  // This component doesn't render anything
  return null;
}

export default LiveUpdates;
